import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { 
  ChevronRight, ShoppingCart, Check, Minus, Plus, 
  Newspaper, Globe, Share2, FileText, ArrowLeft, Tag, Clock, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_QUANTITY = 100;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
        
        // Fetch related products
        const allProducts = await axios.get(`${API}/products?category=${response.data.category}`);
        setRelatedProducts(allProducts.data.filter(p => p.id !== productId).slice(0, 3));
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, navigate]);

  const handleAddToCart = async () => {
    const success = await addToCart(product.id, quantity);
    if (success) {
      toast.success(`${product.name} added to cart!`);
    } else {
      toast.error('Failed to add item to cart');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      print: Newspaper,
      digital: Globe,
      social: Share2,
      advertorial: FileText,
      events: Calendar
    };
    return icons[category] || FileText;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      print: 'Print Advertising',
      digital: 'Digital Advertising',
      social: 'Social Media',
      advertorial: 'Advertorial',
      events: 'Events'
    };
    return labels[category] || category;
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const CategoryIcon = getCategoryIcon(product.category);
  const isOnSale = product.is_on_sale && product.discount_valid;
  const discountPercentage = product.discount_percentage;
  const expiryDate = formatExpiryDate(product.discount_expires_at);
  const finalPrice = product.final_price || product.price;
  const savings = isOnSale ? (product.price - finalPrice) * quantity : 0;

  // The player sits under the image on desktop and inside the info column on
  // mobile, which are different parents, so it is rendered twice and each copy
  // is hidden at the other breakpoint. Both are lazy, so the hidden one never
  // loads.
  const renderVideo = (variant) => {
    if (!product.youtube_embed_url) return null;
    return (
      <div data-testid={`product-video-${variant}`}>
        <div className="aspect-video rounded-xl overflow-hidden bg-slate-900 shadow-lg">
          <iframe
            src={product.youtube_embed_url}
            title={`${product.name} video`}
            className="w-full h-full"
            frameBorder="0"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
        {product.youtube_watch_url && (
          <a
            href={product.youtube_watch_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 text-sm text-slate-500 hover:text-navy-700 transition-colors"
          >
            Watch on YouTube
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-500 hover:text-navy-700">Home</Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <Link to="/products" className="text-slate-500 hover:text-navy-700">Products</Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <Link to={`/products/${product.category}`} className="text-slate-500 hover:text-navy-700">
              {getCategoryLabel(product.category)}
            </Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <span className="text-slate-900 font-medium truncate max-w-[200px]">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Back button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-navy-700 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Product Image */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 shadow-lg">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>
            <div className="absolute top-4 left-4">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium bg-navy-900 text-white shadow-lg">
                <CategoryIcon className="h-4 w-4" />
                {getCategoryLabel(product.category)}
              </span>
            </div>
            
            {/* Sale badge on image */}
            {isOnSale && (
              <div className="absolute top-4 right-4">
                <Badge className="bg-red-600 text-white text-lg font-bold px-4 py-2 shadow-lg">
                  <Tag className="h-4 w-4 mr-2" />
                  {discountPercentage}% OFF
                </Badge>
              </div>
            )}

            {/* Video sits under the image on desktop only */}
            {product.youtube_embed_url && (
              <div className="hidden lg:block mt-6">
                {renderVideo('desktop')}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            {/* Sale banner */}
            {isOnSale && (
              <div className="bg-gradient-to-r from-red-600 to-red-500 text-white rounded-lg p-4 mb-6 shadow-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-5 w-5" />
                  <span className="font-bold text-lg">Limited Time Offer!</span>
                </div>
                <p className="text-red-100">
                  Save {discountPercentage}% on this product
                  {expiryDate && (
                    <span className="block mt-1 flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Offer expires on {expiryDate}
                    </span>
                  )}
                </p>
              </div>
            )}

            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="product-name">
              {product.name}
            </h1>
            
            {/* Pricing */}
            <div className="mb-6">
              {isOnSale ? (
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="price-tag text-4xl text-red-600" data-testid="product-price">
                    {formatPrice(finalPrice)}
                  </span>
                  <span className="text-2xl text-slate-400 line-through">
                    {formatPrice(product.price)}
                  </span>
                  <Badge className="bg-red-100 text-red-700 text-sm">
                    Save {formatPrice(product.price - finalPrice)}
                  </Badge>
                </div>
              ) : (
                <span className="price-tag text-4xl text-navy-900" data-testid="product-price">
                  {formatPrice(finalPrice)}
                </span>
              )}
              <span className="text-slate-500 ml-2">per insertion</span>
            </div>

            <div className="prose prose-slate max-w-none mb-8 overflow-hidden" data-testid="product-description">
              {product.description?.includes('<') ? (
                <div 
                  className="text-lg text-slate-600 font-body rich-text-content"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              ) : (
                <p className="text-lg text-slate-600 font-body whitespace-pre-line">
                  {product.description}
                </p>
              )}
            </div>

            {/* Video sits between the description and Quantity on mobile only */}
            {product.youtube_embed_url && (
              <div className="lg:hidden mb-8">
                {renderVideo('mobile')}
              </div>
            )}

            {/* Quantity selector */}
            <div className="mb-6">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Quantity</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  data-testid="quantity-minus"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.min(MAX_QUANTITY, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-20 text-center"
                  min="1"
                  max={MAX_QUANTITY}
                  data-testid="quantity-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.min(MAX_QUANTITY, quantity + 1))}
                  disabled={quantity >= MAX_QUANTITY}
                  data-testid="quantity-plus"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Total */}
            <div className={`rounded-lg p-4 mb-6 ${isOnSale ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Total</span>
                <span className={`font-heading text-2xl font-bold ${isOnSale ? 'text-red-600' : 'text-navy-900'}`} data-testid="product-total">
                  {formatPrice(finalPrice * quantity)}
                </span>
              </div>
              {isOnSale && savings > 0 && (
                <div className="flex items-center justify-between mt-2 text-sm">
                  <span className="text-green-600 font-medium">You save</span>
                  <span className="text-green-600 font-bold">{formatPrice(savings)}</span>
                </div>
              )}
            </div>

            {/* Add to cart button */}
            <Button
              size="lg"
              className={`w-full h-14 text-lg font-semibold ${isOnSale ? 'bg-red-600 hover:bg-red-700' : 'bg-navy-900 hover:bg-navy-800'} text-white`}
              onClick={handleAddToCart}
              disabled={cartLoading}
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              {cartLoading ? 'Adding...' : 'Add to Cart'}
            </Button>

            {/* Features */}
            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-3 text-slate-600">
                <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span>Secure Stripe checkout</span>
              </div>
              <div className="flex items-center gap-3 text-slate-600">
                <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span>Order confirmation via email</span>
              </div>
              <div className="flex items-center gap-3 text-slate-600">
                <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span>Dedicated support team</span>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-16 md:mt-24">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-8">
              Related Products
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((relatedProduct) => {
                const relatedIsOnSale = relatedProduct.is_on_sale && relatedProduct.discount_valid;
                return (
                  <Link key={relatedProduct.id} to={`/product/${relatedProduct.id}`}>
                    <Card className={`overflow-hidden hover:shadow-lg transition-shadow ${relatedIsOnSale ? 'ring-2 ring-red-500/20' : ''}`}>
                      <div className="aspect-video overflow-hidden bg-slate-100 relative">
                        <img
                          src={relatedProduct.image_url}
                          alt={relatedProduct.name}
                          className="w-full h-full object-cover"
                        />
                        {relatedIsOnSale && (
                          <Badge className="absolute top-2 right-2 bg-red-600 text-white">
                            {relatedProduct.discount_percentage}% OFF
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-heading font-semibold text-slate-900 mb-2">
                          {relatedProduct.name}
                        </h3>
                        <div className="flex items-center gap-2">
                          {relatedIsOnSale ? (
                            <>
                              <span className="price-tag text-xl text-red-600">
                                {formatPrice(relatedProduct.final_price)}
                              </span>
                              <span className="text-sm text-slate-400 line-through">
                                {formatPrice(relatedProduct.price)}
                              </span>
                            </>
                          ) : (
                            <span className="price-tag text-xl text-navy-900">
                              {formatPrice(relatedProduct.final_price || relatedProduct.price)}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
